import { CertificateRequestStatus, CertificateStatus } from "@tfg/db"
import { FilterBuilderProps } from "@tfg/ui"
import { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { CertificateCategory } from "@tfg/types/schemas/certificates"

import {
  FindCertificatesByOrganizationIdRequestFiltersDto,
  useFindCertificateTypesByOrganizationId,
  useFindPurchaseOrdersByOrganizationCertificates,
  useFindBusinessPartnersByOrganizationCertificates,
  useFindProductionSitesByOrganizationCertificates,
} from "~/gen/api"
import { getScoreTranslations } from "~/pages/Brand/Certificates/type"
import { useAuth } from "~/store/auth-context"

const FETCH_LIMIT = "40"
export const useSearchableProductionSites = (organizationId: string) => {
  const [searchQuery, setSearchQuery] = useState("")
  const {
    data: productionSites,
    refetch,
    isLoading,
  } = useFindProductionSitesByOrganizationCertificates(organizationId, {
    search: searchQuery || "",
    limit: FETCH_LIMIT,
  })

  const refetchWithSearch = useCallback(
    (search?: string) => {
      setSearchQuery(search || "")
      return refetch()
    },
    [refetch]
  )

  const options = useMemo(
    () =>
      productionSites?.data.map((site) => ({
        label: site.name ?? "Unknown",
        value: site.id,
      })) ?? [],
    [productionSites]
  )

  return {
    options,
    refetchWithSearch,
    isLoading,
  }
}

export const useSearchableBusinessPartners = (organizationId: string) => {
  const [searchQuery, setSearchQuery] = useState("")
  const {
    data: businessPartners,
    refetch,
    isLoading,
  } = useFindBusinessPartnersByOrganizationCertificates(organizationId, {
    search: searchQuery || "",
    limit: FETCH_LIMIT,
  })

  const refetchWithSearch = useCallback(
    (search?: string) => {
      setSearchQuery(search || "")
      return refetch()
    },
    [refetch]
  )

  const options = useMemo(
    () =>
      businessPartners?.data.map((site) => ({
        label: site.name ?? "Unknown",
        value: site.id,
      })) ?? [],
    [businessPartners]
  )

  return {
    options,
    refetchWithSearch,
    isLoading,
  }
}

export const useSearchableAssociatedPOs = (organizationId: string) => {
  const [searchQuery, setSearchQuery] = useState("")

  const {
    data: associatedPOs,
    refetch,
    isLoading,
  } = useFindPurchaseOrdersByOrganizationCertificates(organizationId, {
    search: searchQuery || "",
    limit: FETCH_LIMIT,
  })

  const refetchWithSearch = useCallback(
    (search?: string) => {
      setSearchQuery(search || "")
      return refetch()
    },
    [refetch]
  )

  const posToOptions = useCallback((pos: string[]) => {
    return pos.map((po) => ({
      label: po,
      value: po,
    }))
  }, [])

  const options = useMemo(
    () => ({
      product: posToOptions(associatedPOs?.data?.product || []),
      component: posToOptions(associatedPOs?.data?.component || []),
      material: posToOptions(associatedPOs?.data?.material || []),
    }),
    [associatedPOs, posToOptions]
  )
  return {
    options,
    refetchWithSearch,
    isLoading,
  }
}

export const useGetCertificatesFiltersOptions = () => {
  const { data: certificateTypes } = useFindCertificateTypesByOrganizationId()
  const { t } = useTranslation(["certificates"])
  const { user, isAdmin } = useAuth()
  const organizationId = user?.organization?.id ?? ""

  const {
    options: productionSiteOptions,
    refetchWithSearch: refetchProductionSites,
    isLoading: isLoadingProductionSites,
  } = useSearchableProductionSites(organizationId)
  const {
    options: businessPartnerOptions,
    refetchWithSearch: refetchBusinessPartners,
    isLoading: isLoadingBusinessPartners,
  } = useSearchableBusinessPartners(organizationId)

  const {
    options: associatedPOOptions,
    refetchWithSearch: refetchAssociatedPOs,
    isLoading: isLoadingAssociatedPOs,
  } = useSearchableAssociatedPOs(organizationId)

  return useMemo(() => {
    const certificateCategoryTranslations: {
      [key in CertificateCategory]: string
    } = {
      [CertificateCategory.TRANSACTION]: t(
        "certificates:certificateCategory.TRANSACTION"
      ),
      [CertificateCategory.SCOPE]: t("certificates:certificateCategory.SCOPE"),
      [CertificateCategory.OTHER_INITIATIVES]: t(
        "certificates:certificateCategory.OTHER_INITIATIVES"
      ),
      [CertificateCategory.UNKNOWN]: t(
        "certificates:certificateCategory.UNKNOWN"
      ),
    }
    const certificateRequestStatusTranslations: {
      [key in Exclude<CertificateRequestStatus, "OPENED" | "CANCELED">]: string
    } = {
      [CertificateRequestStatus.SUBMITTED]: t(
        "certificates:certificateRequestStatus.SUBMITTED"
      ),
      [CertificateRequestStatus.ACCEPTED]: t(
        "certificates:certificateRequestStatus.ACCEPTED"
      ),
      [CertificateRequestStatus.REJECTED]: t(
        "certificates:certificateRequestStatus.REJECTED"
      ),
      ...(isAdmin
        ? {
            [CertificateRequestStatus.CANCELED]: t(
              "certificates:certificateRequestStatus.CANCELED"
            ),
          }
        : {}),
    }

    const certificateStatusTranslations: {
      [key in Exclude<CertificateStatus, "NOT_UPLOADED" | "REJECTED">]: string
    } = {
      [CertificateStatus.PENDING]: t("certificates:certificateStatus.PENDING"),
      [CertificateStatus.PROCESSED]: t(
        "certificates:certificateStatus.PROCESSED"
      ),
    }

    const options: FilterBuilderProps<FindCertificatesByOrganizationIdRequestFiltersDto>["options"] =
      [
        {
          label: t("lists.placeholders.certificateRequestStatus"),
          key: "certificateRequestStatus",
          type: "multiSelect",
          options: Object.entries(certificateRequestStatusTranslations).map(
            ([value, label]) => ({
              label,
              value,
            })
          ),
        },
        {
          label: t("lists.placeholders.certificateStatus"),
          key: "status",
          type: "multiSelect",
          options: Object.entries(certificateStatusTranslations).map(
            ([value, label]) => ({
              label,
              value,
            })
          ),
        },
        {
          label: t("lists.placeholders.certificateType"),
          key: "certificateTypeId",
          type: "multiSelect",
          options:
            certificateTypes?.data?.map((type) => ({
              label: type.name,
              value: type.id,
            })) ?? [],
        },
        {
          label: t("lists.placeholders.category"),
          key: "category",
          type: "multiSelect",
          options: Object.entries(certificateCategoryTranslations).map(
            ([value, label]) => ({
              label,
              value,
            })
          ),
        },
        {
          label: t("lists.placeholders.validFrom"),
          key: "issueDate",
          type: "dateRange",
          options: [],
        },
        {
          label: t("lists.placeholders.createdAt"),
          key: "createdAt",
          type: "dateRange",
          options: [],
        },
        {
          label: t("lists.placeholders.validTo"),
          key: "expiryDate",
          type: "dateRange",
          options: [],
        },
        {
          label: t("lists.placeholders.productionSite"),
          key: "productionSiteId",
          type: "async-multi-select",
          options: productionSiteOptions ?? [],
          asyncRefetch: refetchProductionSites,
          isAsyncLoading: isLoadingProductionSites,
        },
        {
          label: t("lists.placeholders.businessPartner"),
          key: "businessPartnerId",
          type: "async-multi-select",
          options: businessPartnerOptions ?? [],
          asyncRefetch: refetchBusinessPartners,
          isAsyncLoading: isLoadingBusinessPartners,
        },
        {
          label: t("lists.placeholders.componentPOs"),
          key: "componentPurchaseOrderReferences",
          type: "async-multi-select",
          options: associatedPOOptions?.component ?? [],
          asyncRefetch: refetchAssociatedPOs,
          isAsyncLoading: isLoadingAssociatedPOs,
        },
        {
          label: t("lists.placeholders.materialPOs"),
          key: "materialPurchaseOrderReferences",
          type: "async-multi-select",
          options: associatedPOOptions?.material ?? [],
          asyncRefetch: refetchAssociatedPOs,
          isAsyncLoading: isLoadingAssociatedPOs,
        },
        {
          label: t("lists.placeholders.productPOs"),
          key: "productPurchaseOrderReferences",
          type: "async-multi-select",
          options: associatedPOOptions?.product ?? [],
          asyncRefetch: refetchAssociatedPOs,
          isAsyncLoading: isLoadingAssociatedPOs,
        },
      ]

    options.push({
      label: t("lists.placeholders.score"),
      key: "score",
      type: "multiSelect",
      options: Object.entries(getScoreTranslations(t)).map(
        ([score, label]) => ({
          label,
          value: score,
        })
      ),
    })

    return options
  }, [
    t,
    certificateTypes,
    productionSiteOptions,
    isLoadingProductionSites,
    businessPartnerOptions,
    isLoadingBusinessPartners,
    associatedPOOptions,
    isLoadingAssociatedPOs,
    isAdmin,
    refetchAssociatedPOs,
    refetchBusinessPartners,
    refetchProductionSites,
  ])
}
